// src/lib/footprint-editor/frame.js
//
// The authoring frame (design §7.1): the WGS 84 UTM zone of the perimeter's
// centroid, in meters. Every footprint operation happens here, and only the
// result is projected back to lng/lat.
//
// The proj4 definitions are spatial_ref_sys's for EPSG 326xx / 327xx, word for
// word, so the preview runs the projection 151's ST_Transform runs. The shared
// fixture (docs/architecture/padplanner/fixtures/pad_plan_feature_geom_45x8_37.json)
// was written with the same string and proj4 2.20.6.
//
// The revision's local_epsg is the server's (S4a, D-S4-5). Before the first
// save it is NULL; provisionalEpsg() computes what the server will choose, for
// preview only, and the editor adopts the saved value when it comes back.
import proj4 from "proj4";

export function isUtmEpsg(epsg) {
  return Number.isInteger(epsg) && ((epsg >= 32601 && epsg <= 32660) || (epsg >= 32701 && epsg <= 32760));
}

/** admin-api/lib/padplanner/features.js utmEpsgFor(), the same rule. */
export function utmEpsgFor(lon, lat) {
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
  const zone = Math.min(60, Math.floor((lon + 180) / 6) + 1);
  return (lat >= 0 ? 32600 : 32700) + zone;
}

export function utmZone(epsg) {
  if (!isUtmEpsg(epsg)) return null;
  return { zone: epsg % 100, hemisphere: epsg >= 32701 ? "S" : "N" };
}

/** "UTM 13N" for 32613. */
export function utmZoneLabel(epsg) {
  const z = utmZone(epsg);
  return z ? `UTM ${z.zone}${z.hemisphere}` : null;
}

export function proj4Definition(epsg) {
  const z = utmZone(epsg);
  if (!z) throw new Error(`footprint-editor: ${epsg} is not a WGS 84 UTM zone`);
  return `+proj=utm +zone=${z.zone}${z.hemisphere === "S" ? " +south" : ""} +datum=WGS84 +units=m +no_defs`;
}

/**
 * The frame for one EPSG: toLocal([lng, lat]) -> [E, N] meters, and back.
 * @param {number} epsg
 */
export function createFrame(epsg) {
  const def = proj4Definition(epsg);
  const p = proj4("EPSG:4326", def);
  return Object.freeze({
    epsg,
    definition: def,
    label: utmZoneLabel(epsg),
    toLocal: (lngLat) => p.forward([lngLat[0], lngLat[1]]),
    toLngLat: (en) => p.inverse([en[0], en[1]]),
  });
}

// ST_Centroid on the 4326 perimeter is planar in degrees, so this is too: the
// area-weighted centroid of the rings, holes subtracted. Coordinates are taken
// relative to an origin near the shape: the cross products of raw Permian
// lng/lat cancel away about 1e-7 degrees.
function ringCentroid(ring, [ox, oy]) {
  let a = 0, cx = 0, cy = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const x0 = ring[j][0] - ox, y0 = ring[j][1] - oy, x1 = ring[i][0] - ox, y1 = ring[i][1] - oy;
    const k = x0 * y1 - x1 * y0;
    a += k; cx += (x0 + x1) * k; cy += (y0 + y1) * k;
  }
  return { a: a / 2, cx, cy };
}

export function perimeterCentroid(geometry) {
  if (!geometry) return null;
  const polys = geometry.type === "Polygon" ? [geometry.coordinates]
    : geometry.type === "MultiPolygon" ? geometry.coordinates : null;
  if (!polys) return null;
  const o = polys[0]?.[0]?.[0];
  if (!o) return null;
  let A = 0, X = 0, Y = 0;
  for (const poly of polys) {
    for (const [i, ring] of poly.entries()) {
      const { a, cx, cy } = ringCentroid(ring, o);
      // Exterior positive, holes negative, whatever the winding.
      const s = i === 0 ? 1 : -1;
      A += s * Math.abs(a); X += s * Math.sign(a) * cx; Y += s * Math.sign(a) * cy;
    }
  }
  if (!A) return null;
  return [o[0] + X / (6 * A), o[1] + Y / (6 * A)];
}

/** What the server will set local_epsg to on the first save; preview only. */
export function provisionalEpsg(perimeter) {
  const c = perimeterCentroid(perimeter);
  return c ? utmEpsgFor(c[0], c[1]) : null;
}
